/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Component for Advanced Filters Query Builder
 */
import _ from 'lodash';
import { Badge, Button, Flex, Modal, Space } from 'antd';
import { Close, Filter } from '@icon-park/react';
import { JsonTree } from '@react-awesome-query-builder/antd';
import { useCallback, useEffect, useState } from 'react';

import useFields from 'lib/SearchQueryBuilder/hooks/useFields';
import { IFieldsData } from 'lib/SearchQueryBuilder/config/entity.config';
import { QueryBuilder } from 'lib/SearchQueryBuilder';
import { YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';

interface IProps {
	schema: IFieldsData[];
	onSubmit: (value: AnyType) => void;
	initialValue: AnyType;
	isSearch: boolean;
}

const countRules = (tree: AnyType): number => {
	if (!tree?.type) {
		return 0;
	}
	if (tree.type === 'rule') {
		return 1;
	}
	if (tree.children1) {
		return _.reduce(_.values(tree.children1), (acc: number, child: AnyType) => acc + countRules(child), 0);
	}
	return 0;
};

/**
 * FilterClaimStatusData
 * @description Component for Filter Claim Status Data
 */
const QueryFilter: React.FC<IProps> = ({ schema, initialValue, onSubmit, isSearch }) => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState(initialValue);
	const fields = useFields(schema);

	const onFilter = () => {
		onSubmit(query);
		setOpen(false);
	};

	useEffect(() => {
		setQuery(initialValue as JsonTree);
	}, [initialValue]);

	const resetFilters = () => {
		setQuery(initialValue as JsonTree);
	};

	const clearFilters = () => {
		setQuery({});
		onSubmit({});
		setOpen(false);
	};

	const closeFilters = () => {
		setOpen(false);
	};

	const getAdvancedFilters = useCallback(() => {
		return (
			<Modal
				data-testid='er-filter-modal'
				title='Create Filter'
				open={true}
				width={1050}
				maskClosable={false}
				onCancel={closeFilters}
				footer={
					<Flex justify='space-between' gap={16}>
						<Button data-testid='er-filter-reset' onClick={resetFilters} disabled={_.isEqual(query, initialValue)}>
							{'Reset'}
						</Button>
						<Flex gap={16}>
							<Button data-testid='er-filter-cancel' type='text' onClick={closeFilters}>
								{'Cancel'}
							</Button>
							<Button
								data-testid='er-filter-apply'
								type='primary'
								disabled={_.isEqual(query, initialValue)}
								onClick={onFilter}
							>
								{'Apply'}
							</Button>
						</Flex>
					</Flex>
				}
			>
				<QueryBuilder dateFormat={YEAR_MONTH_DATE_FORMAT} value={query} fields={fields} onChange={setQuery} />
			</Modal>
		);
	}, [schema, query, setQuery, closeFilters, onFilter, resetFilters]);

	return (
		<>
			{_.isEmpty(initialValue) || isSearch ? (
				<Button
					data-testid='er-filter-trigger'
					type='default'
					icon={<Filter />}
					iconPosition='start'
					onClick={() => setOpen(true)}
				/>
			) : (
				<Space.Compact>
					<Button
						data-testid='er-filter-applied-trigger'
						color='primary'
						variant='outlined'
						title='Filters Applied'
						icon={<Filter theme='filled' size={16} />}
						onClick={() => setOpen(true)}
					>
						<Badge color='green' count={countRules(initialValue)} />
					</Button>
					<Button
						data-testid='er-filter-clear'
						color='primary'
						variant='outlined'
						title='Clear'
						className='hover:text-[#FF7875]'
						icon={<Close />}
						onClick={clearFilters}
					/>
				</Space.Compact>
			)}
			{open && getAdvancedFilters()}
		</>
	);
};

export default QueryFilter;
